import request from '../request';
import services from '../../services';
import { STATE, GMI} from '../../constants';
import { isArrayNotEmpty } from '../../utils/js/array';
import { batchActions } from '../../utils/redux';

export const { EXTEND_RENTAL } = STATE.GMA;

/**
 * @private
 * @param {Object} tickets
 * @returns {Object} {type: string, path: [], value: string}
 */
function setExtendRentalTicket(tickets) {
  return {type: GMI.SET_GMI_STATE, path: EXTEND_RENTAL.ELIGIBILITY, value: tickets};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: string}
 */
function clearExtendRentalTicket() {
  return {type: GMI.DELETE_GMI_STATE, path: EXTEND_RENTAL.ELIGIBILITY, value: {}};
}

/**
 * @private
 * @param {Object} ticket
 * @returns {Object} {type: string, path: [], value: string}
 */
function setInitiateExtendRentalTicket(ticket) {
  return {type: GMI.SET_GMI_STATE, path: EXTEND_RENTAL.INITIATE, value: ticket};
}

/**
 * @private
 * @param {Object} ticket
 * @returns {Object} {type: string, path: [], value: string}
 */
function clearInitiateExtendRentalState() {
  return {type: GMI.DELETE_GMI_STATE, path: EXTEND_RENTAL.INITIATE, value: {}};
}

/**
 * @private
 * @param {Object} ticket
 * @returns {Object} {type: string, path: [], value: string}
 */
function setCommitExtendRentalTicket(ticket) {
  return {type: GMI.SET_GMI_STATE, path: EXTEND_RENTAL.COMMIT, value: ticket};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: string}
 */
function clearCommitExtendRentalState() {
  return {type: GMI.DELETE_GMI_STATE, path: EXTEND_RENTAL.COMMIT, value: {}};
}

/**
 * @name checkRentalExtendEligibility
 * @desc Make the GMA reservations/extend/eligibility/ call and update the state
 * @param {string} [ticketId]
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.checkRentalExtendEligibility(ticketId));
 */
export function checkRentalExtendEligibility(ticketId, requestOpts = {}) {
  return (dispatch, getState) => {
    const payload = {
      ticketId
    };
    let tickets = getState().getIn(EXTEND_RENTAL.ELIGIBILITY).toJS();
    return dispatch(request({
      service: services.gma.reservations.checkRentalExtendEligibility,
      data: {payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        const { ticket_number } = resp.ticket;
        const newTicket = {
          [ticket_number]: resp.ticket
        };
        tickets = Object.assign({}, tickets,  newTicket);
        dispatch(setExtendRentalTicket(tickets));
        return tickets;
      } else {
        return resp;
      }
    });
  };
}
/**
 * @name initiateRentalExtend
 * @desc Make the GMA /reservations/extend/initiate/ call and update the state
 * @param {String} [ticketId]
 * @param {String} [return_time]
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.initiateRentalExtend(data));
 */
export function initiateRentalExtend(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.initiateRentalExtend,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setInitiateExtendRentalTicket(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}

/**
 * @name commitRentalExtend
 * @desc Make the GMA /reservations/extend/commit/ using the extend_rental.initiate state
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.commitRentalExtend());
 */
export function commitRentalExtend(requestOpts = {}) {
  return (dispatch, getState) => {
    const initiateExtendState = getState().getIn(EXTEND_RENTAL.INITIATE).toJS();
    const { return_time } = initiateExtendState;
    const payload = {
      return_time,
      ticketSessionId: initiateExtendState.ticket_session_id
    };
    return dispatch(request({
      service: services.gma.reservations.commitRentalExtend,
      data: {payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setCommitExtendRentalTicket(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}

/**
 * @name clearExtendRentalState
 * @desc Clear the extend rental state use opts as false if some state
 * doesn't need to be cleared
 * @param {Object} [opts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.clearExtendRentalState(opts));
 * const opts = {
 *     eligibility: true,
 *     initiate: true,
 *     commit: false
 * }
 */
export function clearExtendRentalState(opts = {}) {
  return (dispatch) => {
    const {
      eligibility = true,
      initiate = true,
      commit = true
    } = opts;
    const batchedActions = [];
    if (eligibility) batchedActions.push(clearExtendRentalTicket());
    if (initiate) batchedActions.push(clearInitiateExtendRentalState());
    if (commit) batchedActions.push(clearCommitExtendRentalState());
    if (batchedActions.length !== 0) dispatch(batchActions(...batchedActions));
  };
}
